import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './AuthPopup.css';

const stripPhone = (val) => (val || '').replace(/\D/g, '');
const formatPhoneLocal = (digits) => {
  let d = digits.slice(0, 9);
  if (d.length <= 2) return d;
  if (d.length <= 5) return d.slice(0, 2) + ' ' + d.slice(2);
  if (d.length <= 7) return d.slice(0, 2) + ' ' + d.slice(2, 5) + ' ' + d.slice(5);
  return d.slice(0, 2) + ' ' + d.slice(2, 5) + ' ' + d.slice(5, 7) + ' ' + d.slice(7);
};

export default function AuthPopup({ isOpen, onClose }) {
  const { login, register, toggleAuthModal, authModalTab } = useAuth();
  const [activeTab, setActiveTab] = useState(authModalTab);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showTelegramTip, setShowTelegramTip] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [forgotForm, setForgotForm] = useState({ email: '' });
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    phone: '',
    telegram: '',
    password: '',
    password_confirmation: '',
  });
  const [regPhoneDigits, setRegPhoneDigits] = useState('');

  const tab = activeTab;

  const close = () => {
    setError('');
    setSuccessMessage('');
    onClose?.();
    toggleAuthModal(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await login(loginForm.email, loginForm.password);
      setLoginForm({ ...loginForm, password: '' });
      close();
    } catch (err) {
      setError(err.message || 'Помилка авторизації. Перевірте дані.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      const { forgotPassword } = await import('../services/authApi');
      const res = await forgotPassword(forgotForm.email);
      setSuccessMessage(res.message || 'Посилання для відновлення пароля надіслано на ваш email.');
      setForgotForm({ email: '' });
    } catch (err) {
      setError(err.message || 'Помилка. Спробуйте ще раз.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerForm.password !== registerForm.password_confirmation) {
      setError('Паролі не співпадають');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const phoneData = regPhoneDigits.length > 0 ? '+380' + regPhoneDigits : null;
      await register({
        ...registerForm,
        phone: phoneData,
      });
      setRegisterForm({
        name: '',
        email: '',
        phone: '',
        telegram: '',
        password: '',
        password_confirmation: '',
      });
      setRegPhoneDigits('');
      close();
    } catch (err) {
      setError(err.message || 'Помилка реєстрації. Можливо, email або телефон вже існують.');
    } finally {
      setIsLoading(false);
    }
  };

  const onRegPhoneInput = (e) => {
    let raw = stripPhone(e.target.value);
    if (raw.startsWith('380')) raw = raw.slice(3);
    else if (raw.startsWith('0')) raw = raw.slice(1);
    setRegPhoneDigits(raw.slice(0, 9));
  };

  React.useEffect(() => {
    setActiveTab(authModalTab);
  }, [authModalTab, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="auth-popup-overlay">
      <div className="auth-popup-backdrop" onClick={close} />
      <div className="auth-popup-modal">
        <button type="button" className="auth-popup-close" onClick={close} aria-label="Закрити">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {tab !== 'forgot_password' && (
          <div className="auth-popup-tabs">
            <button
              type="button"
              className={`auth-popup-tab ${tab === 'login' ? 'active' : ''}`}
              onClick={() => setActiveTab('login')}
            >
              Вхід
            </button>
            <button
              type="button"
              className={`auth-popup-tab ${tab === 'register' ? 'active' : ''}`}
              onClick={() => setActiveTab('register')}
            >
              Реєстрація
            </button>
          </div>
        )}

        <div className="auth-popup-body">
          {error && (
            <div className="auth-popup-error">{error}</div>
          )}

          {tab === 'login' && (
            <form onSubmit={handleLogin} className="auth-popup-form">
              <div className="auth-popup-field">
                <label>Email</label>
                <input
                  type="email"
                  required
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  placeholder="ваша@пошта.com"
                />
              </div>
              <div className="auth-popup-field">
                <label>Пароль</label>
                <input
                  type="password"
                  required
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
              <div className="auth-popup-forgot">
                <button type="button" onClick={() => setActiveTab('forgot_password')}>
                  Забули пароль?
                </button>
              </div>
              <button type="submit" className="auth-popup-submit main-btn" disabled={isLoading}>
                {isLoading ? 'Зачекайте...' : 'Увійти'}
              </button>
            </form>
          )}

          {tab === 'register' && (
            <form onSubmit={handleRegister} className="auth-popup-form">
              <div className="auth-popup-field">
                <label>Ім'я або назва гурту</label>
                <input
                  type="text"
                  required
                  value={registerForm.name}
                  onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                  placeholder="Іван Петренко"
                />
              </div>
              <div className="auth-popup-field">
                <label>Email</label>
                <input
                  type="email"
                  required
                  value={registerForm.email}
                  onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                  placeholder="ваша@пошта.com"
                />
              </div>
              <div className="auth-popup-field">
                <label>Телефон (необов'язково)</label>
                <div className="auth-popup-phone">
                  <span className="auth-popup-phone-prefix">+380</span>
                  <input
                    type="tel"
                    value={formatPhoneLocal(regPhoneDigits)}
                    onChange={onRegPhoneInput}
                    maxLength={12}
                    placeholder="XX XXX XX XX"
                  />
                </div>
              </div>
              <div className="auth-popup-field">
                <div className="auth-popup-label-row">
                  <label>Telegram (необов'язково)</label>
                  <button
                    type="button"
                    className="auth-popup-tip-btn"
                    onMouseEnter={() => setShowTelegramTip(true)}
                    onMouseLeave={() => setShowTelegramTip(false)}
                  >
                    ?
                  </button>
                  {showTelegramTip && (
                    <div className="auth-popup-tip">
                      💬 Якщо ви залишите свій Telegram, ми не потурбуємо вас дзвінком, а зв'яжемось з вами в Telegram.
                    </div>
                  )}
                </div>
                <div className="auth-popup-telegram">
                  <span className="auth-popup-telegram-prefix">@</span>
                  <input
                    type="text"
                    value={registerForm.telegram}
                    onChange={(e) => setRegisterForm({ ...registerForm, telegram: e.target.value })}
                    placeholder="username"
                  />
                </div>
              </div>
              <div className="auth-popup-field">
                <label>Пароль</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={registerForm.password}
                  onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                  placeholder="Мінімум 8 символів"
                />
              </div>
              <div className="auth-popup-field">
                <label>Підтвердження пароля</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={registerForm.password_confirmation}
                  onChange={(e) => setRegisterForm({ ...registerForm, password_confirmation: e.target.value })}
                  placeholder="Повторіть пароль"
                />
              </div>
              <button type="submit" className="auth-popup-submit main-btn" disabled={isLoading}>
                {isLoading ? 'Зачекайте...' : 'Зареєструватись'}
              </button>
            </form>
          )}

          {tab === 'forgot_password' && (
            <form onSubmit={handleForgotPassword} className="auth-popup-form">
              <div className="auth-popup-forgot-header">
                <h3>Відновлення пароля</h3>
                <p>Введіть email, пов'язаний з вашим акаунтом, і ми надішлемо посилання для відновлення.</p>
              </div>
              {successMessage && (
                <div className="auth-popup-success">{successMessage}</div>
              )}
              <div className="auth-popup-field">
                <label>Email</label>
                <input
                  type="email"
                  required
                  value={forgotForm.email}
                  onChange={(e) => setForgotForm({ email: e.target.value })}
                  placeholder="ваша@пошта.com"
                />
              </div>
              <button type="submit" className="auth-popup-submit main-btn" disabled={isLoading}>
                {isLoading ? 'Відправляємо...' : 'Надіслати посилання'}
              </button>
              <button
                type="button"
                className="auth-popup-back"
                onClick={() => { setActiveTab('login'); setSuccessMessage(''); }}
              >
                Повернутись до входу
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
